/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import Shortcuts from '@intelligence/components/GenreSelect/components/Shortcuts';
import RelationSelect from '@intelligence/components/GenreSelect/components/RelationSelect';
import CheckboxbarPanel from '@intelligence/components/GenreSelect/components/CheckboxbarPanel';
import useScrollActiveIntoView from '@intelligence/components/GenreSelect/_hooks/useScrollActiveIntoView';
import { Relation, ShortcutActionType } from '@intelligence/components/GenreSelect/enums';

import type {
  IBaseProps,
  IShortcut,
  IGenreCheckboxOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';
import type { IBatchOperation } from '@intelligence/components/GenreSelect/components/CheckboxbarPanel';

import '@intelligence/components/GenreSelect/components/CheckboxbarPanels/index.scss';

interface ICheckboxbarPanelsProps extends IBaseProps {
  // 重置当前激活品类索引的标识
  currentIndexResetFlag?: any;
  // 外部受控的当前激活主品类索引，用于跨开闭保留选中的主品类链路
  activeIndices?: number[];
  onActiveIndicesChange?: (indices: number[]) => void;
  showRelationSelectUI?: boolean;
  shortcuts?: IShortcut[];
  // 主、副品类Checkbox的Options集合
  allGenres: IGenreCheckboxOptionWithChildren[];
  // 搜索过滤后的主、副品类Checkbox的Options集合
  genres: IGenreCheckboxOptionWithChildren[];
  onSubGenreCheckedChange?: (genre: string, relation: Relation, subGenre: string, checked: boolean) => void;
  onSubGenreAllCheckedChange?: (genre: string, relation: Relation, checked: boolean) => void;
  onGenreCheckedChange?: (genre: string, relation: Relation, checked: boolean) => void;
  onGenreAllCheckedChange?: (checked: boolean) => void;
  onGenreRelationChange?: (genre: string, relation: Relation) => void;
  onShortcutClick?: (shortcut: IShortcut) => void;
}

const isIndeterminate = (genre?: IGenreCheckboxOptionWithChildren) => !!genre?.indeterminate;

/**
 * 判断指定品类快捷勾选按钮是否禁用
 * Compose模式：存在某个主品类下的副品类跟指定品类快捷勾选按钮匹配并且该副品类没有勾选时，则不禁用，反之禁用
 * Replace模式：只有当当前勾选的品类跟快捷勾选按钮配置的品类完全一样时，才判定为高亮
 * @param type 快捷按钮勾选模式
 * @param shortcut 品类快捷勾选按钮value（其实就是某个副品类value）
 * @param genres 当前主、副品类Checkbox的Options集合
 * @returns 品类快捷勾选按钮是否禁用
 */
const getShortcutDisabled = (type: ShortcutActionType, shortcut: string, genres?: IGenreCheckboxOptionWithChildren[]): boolean => {
  if (!Array.isArray(genres) || !genres.length) return false;
  if (!shortcut) return false;

  // 统计匹配的副品类和已勾选的副品类
  let matchedLength = 0;
  let matchedAndCheckedLength = 0;
  let totalCheckedLength = 0;

  for (const genre of genres) {
    const subGenres = genre?.children;
    if (!Array.isArray(subGenres) || !subGenres.length) continue;
    for (const subGenre of subGenres) {
      if (subGenre?.value === shortcut) {
        matchedLength += 1;
        if (subGenre?.checked) {
          matchedAndCheckedLength += 1;
        }
      }
      if (subGenre?.checked) {
        totalCheckedLength += 1;
      }
    }
  }

  if (matchedLength === 0) return false;

  // Replace 模式：只有当匹配的品类都已勾选，且没有其他已勾选的品类时，才高亮
  if (type === ShortcutActionType.Replace) {
    return matchedLength === matchedAndCheckedLength && matchedAndCheckedLength === totalCheckedLength;
  }

  // Compose 模式：只要匹配的品类都已勾选，就高亮
  return matchedLength === matchedAndCheckedLength;
};

/**
 * 给品类快捷勾选按钮集合追加disabled状态
 * @param shortcuts 品类快捷勾选按钮集合
 * @param genres 当前主、副品类Checkbox的Options集合
 * @returns 品类快捷勾选按钮集合（带disabled状态）
 */
const addShortcutsDisabledState = (
  shortcuts?: IShortcut[],
  genres?: IGenreCheckboxOptionWithChildren[],
): IShortcut[] => {
  if (!Array.isArray(shortcuts) || !shortcuts.length) return [];
  if (!Array.isArray(genres) || !genres.length) return shortcuts;
  return shortcuts.map((shortcut) => {
    const shortcutDisabled = getShortcutDisabled(shortcut?.type, shortcut?.value, genres);
    return { ...shortcut, disabled: shortcutDisabled };
  });
};

const CheckboxbarPanels: React.FC<ICheckboxbarPanelsProps> = (props) => {
  const {
    style,
    className,
    currentIndexResetFlag,
    activeIndices,
    onActiveIndicesChange,
    showRelationSelectUI,
    shortcuts: shortcutsProps,
    allGenres,
    genres,
    onSubGenreCheckedChange,
    onSubGenreAllCheckedChange,
    onGenreCheckedChange,
    onGenreAllCheckedChange,
    onGenreRelationChange,
    onShortcutClick,
  } = props;

  const shortcuts = addShortcutsDisabledState(shortcutsProps, allGenres);

  const showShortcutsUI = Array.isArray(shortcuts) && shortcuts.length > 0;

  const { formatMessage } = useIntl();

  // 当前激活的主品类索引，初始值优先取外部受控值，跨开闭保留选中的主品类链路
  const [currentGenreIndex, setCurrentGenreIndex] = useState<number>(activeIndices?.[0] ?? 0);

  const syncCurrentGenreIndex = (index: number) => {
    setCurrentGenreIndex(index);
    onActiveIndicesChange?.([index]);
  };

  const currentGenre = useMemo<IGenreCheckboxOptionWithChildren | null>(() => {
    return genres?.[currentGenreIndex] || null;
  }, [currentGenreIndex, genres]);

  // 主品类面板：挂载及激活主品类变化时，把激活项滚动到可视区（仅在不可见时滚动）
  const mainGenreScrollRef = useScrollActiveIntoView([currentGenreIndex, genres]);

  const mainBatchOperation = useMemo<IBatchOperation>(() => {
    if (!Array.isArray(genres) || !genres.length) return -1;
    return !genres.some((genre) => genre?.checked || genre?.indeterminate) ? 1 : 0;
  }, [genres]);

  const subBatchOperation = useMemo<IBatchOperation>(() => {
    const currentSubGenres = currentGenre?.children;
    if (!Array.isArray(currentSubGenres) || !currentSubGenres.length) return -1;
    return !currentSubGenres.some((subGenre) => subGenre?.checked) ? 1 : 0;
  }, [currentGenre]);

  const handleSubGenreCheckedChange = (subGenre: string, checked: boolean) => {
    const genre = currentGenre?.value;
    const relation = currentGenre?.relation;
    if (genre && relation) {
      onSubGenreCheckedChange?.(genre, relation, subGenre, checked);
    }
  };

  const handleSubGenreAllCheckedChange = (allChecked: boolean) => {
    const genre = currentGenre?.value;
    const relation = currentGenre?.relation;
    if (genre && relation) {
      onSubGenreAllCheckedChange?.(genre, relation, allChecked);
    }
  };

  const handleGenreCheckedChange = (genreValue: string, checked: boolean) => {
    const genre = genres?.find((genre) => genre?.value === genreValue);
    const relation = genre?.relation || Relation.Or;
    onGenreCheckedChange?.(genreValue, relation, checked);
  };

  const renderShortcutsUI = () => {
    if (!showShortcutsUI) return undefined;
    return <Shortcuts shortcuts={shortcuts} onClick={onShortcutClick} />;
  };

  const renderRelationSelectUI = () => {
    if (!showRelationSelectUI) return undefined;
    return (
      <RelationSelect
        value={currentGenre?.relation}
        onChange={(relation) => {
          const genre = currentGenre?.value;
          if (genre) {
            onGenreRelationChange?.(genre, relation);
          }
        }}
      />
    );
  };

  // 搜索关键字变化时重置到首个主品类；空关键字（含首次挂载）不重置，保留外部传入的激活索引
  useEffect(() => {
    if (!currentIndexResetFlag) return;
    syncCurrentGenreIndex(0);
  }, [currentIndexResetFlag]);

  // 外部受控索引变化时同步内部状态
  useEffect(() => {
    if (!Array.isArray(activeIndices)) return;
    setCurrentGenreIndex(activeIndices[0] ?? 0);
  }, [activeIndices]);

  return (
    <div style={style} className={classnames('intelligence-genre-select-out', className)}>
      <div className="intelligence-genre-select-panel">
        {/* 主品类Checkbox Panel */}
        <CheckboxbarPanel
          showRightArrow
          className="panel-left"
          level={0}
          checkboxListClassName={classnames({ 'renderMainGenres-tagsVision': showShortcutsUI })}
          containerRef={mainGenreScrollRef}
          title={formatMessage({ id: `dbi_main_genre` })}
          batchOperation={mainBatchOperation}
          active={(genreValue) => genreValue === currentGenre?.value}
          indeterminate={isIndeterminate}
          genres={genres}
          onChange={handleGenreCheckedChange}
          onClick={(genreIndex) => {
            syncCurrentGenreIndex(genreIndex);
          }}
          onOperation={onGenreAllCheckedChange}
          header={renderShortcutsUI()}
        />
        {/* 中间的分割线 */}
        <div className="panel-divider-line"></div>
        {/* 副品类Checkbox Panel */}
        <CheckboxbarPanel
          className="panel-right"
          checkboxListClassName={classnames({
            'has-relation': showRelationSelectUI,
          })}
          level={1}
          title={formatMessage({ id: `dbi_sub_genre` })}
          batchOperation={subBatchOperation}
          genres={currentGenre?.children as IGenreCheckboxOptionWithChildren[]}
          onChange={handleSubGenreCheckedChange}
          onClick={(subGenreIndex, subGenre, checked) => {
            handleSubGenreCheckedChange(subGenre, checked);
          }}
          onOperation={handleSubGenreAllCheckedChange}
          header={renderRelationSelectUI()}
        />
      </div>
    </div>
  );
};

export default CheckboxbarPanels;
